import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Archive, Building2, CheckCircle2, Layers, Loader2, PowerOff, ShieldAlert, Trash2, Users, UserX } from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Skeleton } from "./ui/skeleton";
import { Switch } from "./ui/switch";
import { api, errMsg } from "../lib/api";

const CONFIRM_WORD = "DELETE";
const BUCKET_META = {
  properties: { label: "Properties", one: "property", Icon: Building2, blocking: true },
  staff_active: { label: "Active staff mapped", one: "active staff member mapped", Icon: Users, blocking: true },
  children: { label: "Child records", one: "child record", Icon: Layers, blocking: true },
  other: { label: "Other records", one: "other record", Icon: Layers, blocking: true },
  properties_trash: { label: "In recycle bin", one: "in recycle bin", Icon: Archive, blocking: false },
  staff_inactive: { label: "Inactive staff mapped", one: "inactive staff member mapped", Icon: UserX, blocking: false },
};
const fmt = (n) => (n || 0).toLocaleString("en-IN");
const countLabel = (k, n) => `${fmt(n)} ${n === 1 ? BUCKET_META[k].one : BUCKET_META[k].label.toLowerCase()}`;

/**
 * Phase 148 — Delete Selected (bulk) for master tables.
 *   POST /masters/{type}/bulk-dependencies -> aggregated warning + per-row verdicts
 *   POST /masters/{type}/bulk-delete       -> deletes the unlinked rows, deactivates (or skips) the linked ones
 * Props: open, onOpenChange, code, masterType, ids (string[]), nameOf(id) -> label, onDone()
 */
export const BulkDeleteMastersDialog = ({ open, onOpenChange, code, masterType, ids, nameOf, onDone }) => {
  const [scan, setScan] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [deactivateBlocked, setDeactivateBlocked] = useState(true);
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setScan(null); setLoadError(null); setResult(null); setWord(""); setDeactivateBlocked(true);
    api.post(`/t/${code}/masters/${masterType}/bulk-dependencies`, { ids })
      .then((r) => { if (!cancelled) setScan(r.data); })
      .catch((e) => { if (!cancelled) setLoadError(errMsg(e)); });
    return () => { cancelled = true; };
  }, [open, code, masterType, ids]);

  const plural = scan?.label?.toLowerCase() || "rows";
  const singular = scan?.singular?.toLowerCase() || "row";
  const blockedActive = useMemo(() => (scan?.items || []).filter((it) => !it.can_delete && !it.guards?.length && it.is_active).length, [scan]);
  const blockedInactive = (scan?.blocked || 0) - blockedActive;
  const willDelete = scan?.deletable || 0;
  const willDeactivate = deactivateBlocked ? blockedActive : 0;
  const willSkip = (scan?.blocked || 0) - willDeactivate + (scan?.guarded || 0);
  const needsWord = willDelete > 0;
  const nothingToDo = scan && willDelete === 0 && willDeactivate === 0;
  const canConfirm = scan && !busy && !nothingToDo && (!needsWord || word.trim().toUpperCase() === CONFIRM_WORD);
  const warnBuckets = Object.entries(BUCKET_META).filter(([k]) => scan?.totals?.[k] > 0);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/masters/${masterType}/bulk-delete`, { ids, deactivate_blocked: deactivateBlocked, confirm: true });
      setResult(r.data);
      const c = r.data.counts || {};
      const bits = [c.deleted ? `${c.deleted} deleted` : null, c.deactivated ? `${c.deactivated} deactivated` : null, c.skipped ? `${c.skipped} skipped` : null].filter(Boolean);
      toast.success(`${scan.label}: ${bits.join(", ") || "nothing changed"}`);
      onDone?.(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmLabel = [willDelete ? `Delete ${fmt(willDelete)}` : null, willDeactivate ? `Deactivate ${fmt(willDeactivate)}` : null].filter(Boolean).join(" · ") || "Nothing to do";

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="sm:max-w-2xl" data-testid="bulk-delete-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-destructive" /> Delete selected {plural}
          </DialogTitle>
          <DialogDescription data-testid="bulk-delete-subtitle">
            {ids.length} {ids.length === 1 ? singular : plural} selected. Rows with linked records are never deleted permanently.
          </DialogDescription>
        </DialogHeader>

        {loadError && (
          <Alert variant="destructive" data-testid="bulk-delete-error">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Could not check linked records</AlertTitle>
            <AlertDescription>{loadError}</AlertDescription>
          </Alert>
        )}
        {!scan && !loadError && (
          <div className="space-y-2" data-testid="bulk-delete-loading" aria-busy="true">
            <Skeleton className="h-16 w-full" /><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" />
          </div>
        )}

        {scan && !result && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2" data-testid="bulk-delete-summary">
              <div className="rounded-lg border p-3">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Will be deleted</p>
                <p className="text-2xl font-semibold tabular-nums" data-testid="bulk-delete-count-deletable">{fmt(willDelete)}</p>
              </div>
              <div className={`rounded-lg border p-3 ${scan.blocked ? "border-amber-300 bg-amber-50/60" : ""}`}>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Have linked records</p>
                <p className={`text-2xl font-semibold tabular-nums ${scan.blocked ? "text-amber-700" : ""}`} data-testid="bulk-delete-count-blocked">{fmt(scan.blocked)}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Cannot be touched</p>
                <p className="text-2xl font-semibold tabular-nums" data-testid="bulk-delete-count-guarded">{fmt(scan.guarded + (scan.missing?.length || 0))}</p>
              </div>
            </div>

            {scan.blocked > 0 || scan.guarded > 0 ? (
              <Alert data-testid="bulk-delete-warning" className="border-amber-300 bg-amber-50 text-amber-900 [&>svg]:text-amber-600">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>
                  {fmt(scan.blocking_total)} active record{scan.blocking_total === 1 ? " is" : "s are"} linked to {fmt(scan.blocked + scan.guarded)} of the selected {plural}
                </AlertTitle>
                <AlertDescription className="text-amber-900/80 space-y-2">
                  <p>{scan.message}</p>
                  {warnBuckets.length > 0 && (
                    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs" data-testid="bulk-delete-linked-totals">
                      {warnBuckets.map(([k, m]) => (
                        <li key={k} className="flex items-center gap-1.5" data-testid={`bulk-delete-total-${k}`}>
                          <m.Icon className="h-3.5 w-3.5" /> <span className="font-semibold tabular-nums">{fmt(scan.totals[k])}</span> {scan.totals[k] === 1 ? m.one : m.label.toLowerCase()}
                          {!m.blocking && <span className="text-amber-900/60">(cleared on delete)</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </AlertDescription>
              </Alert>
            ) : (
              <Alert data-testid="bulk-delete-ok" className="border-emerald-200 bg-emerald-50/60 text-emerald-900 [&>svg]:text-emerald-600">
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>No active records are linked</AlertTitle>
                <AlertDescription className="text-emerald-900/80">{scan.message}</AlertDescription>
              </Alert>
            )}

            <ul className="max-h-56 overflow-y-auto rounded-lg border divide-y text-sm" data-testid="bulk-delete-items">
              {scan.items.map((it) => {
                const verdict = it.guards?.length ? "guarded" : it.can_delete ? "delete" : it.is_active ? "linked" : "linked_inactive";
                return (
                  <li key={it.id} className="flex items-center justify-between gap-3 px-3 py-1.5" data-testid={`bulk-delete-item-${it.id}`}>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{it.name || nameOf?.(it.id) || it.id.slice(0, 8)}</p>
                      {verdict !== "delete" && (
                        <p className="text-xs text-muted-foreground truncate">
                          {it.guards?.length ? it.guards[0]
                            : Object.entries(it.buckets).filter(([k]) => BUCKET_META[k]?.blocking).map(([k, n]) => countLabel(k, n)).join(" · ")}
                        </p>
                      )}
                    </div>
                    {verdict === "delete" && <Badge variant="outline" className="shrink-0 border-destructive/40 text-destructive" data-testid={`bulk-delete-verdict-${it.id}`}>Delete</Badge>}
                    {verdict === "linked" && <Badge variant="outline" className="shrink-0 border-amber-400 text-amber-700" data-testid={`bulk-delete-verdict-${it.id}`}>{deactivateBlocked ? "Deactivate" : "Skip"}</Badge>}
                    {verdict === "linked_inactive" && <Badge variant="outline" className="shrink-0 text-muted-foreground" data-testid={`bulk-delete-verdict-${it.id}`}>Already inactive · skip</Badge>}
                    {verdict === "guarded" && <Badge variant="outline" className="shrink-0 text-muted-foreground" data-testid={`bulk-delete-verdict-${it.id}`}><ShieldAlert className="h-3 w-3 mr-1" /> Protected</Badge>}
                  </li>
                );
              })}
              {scan.missing?.length > 0 && (
                <li className="px-3 py-1.5 text-xs text-muted-foreground" data-testid="bulk-delete-missing">{scan.missing.length} selected row(s) no longer exist and will be ignored.</li>
              )}
            </ul>

            {scan.blocked > 0 && (
              <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2" data-testid="bulk-delete-deactivate-row">
                <div>
                  <Label htmlFor="bulk-deactivate" className="text-sm">Deactivate the linked {scan.blocked === 1 ? singular : plural} instead of skipping</Label>
                  <p className="text-xs text-muted-foreground">
                    They stay on existing records and reports but disappear from dropdowns for new entries.
                    {blockedInactive > 0 && ` ${blockedInactive} ${blockedInactive === 1 ? "is" : "are"} already inactive.`}
                  </p>
                </div>
                <Switch id="bulk-deactivate" checked={deactivateBlocked} onCheckedChange={setDeactivateBlocked} data-testid="bulk-delete-deactivate-switch" />
              </div>
            )}

            {needsWord && (
              <div className="space-y-1.5">
                <Label htmlFor="bulk-delete-word" className="text-sm">
                  Type <span className="font-mono font-semibold">{CONFIRM_WORD}</span> to permanently delete {fmt(willDelete)} {willDelete === 1 ? singular : plural}
                </Label>
                <Input id="bulk-delete-word" value={word} onChange={(e) => setWord(e.target.value)} placeholder={CONFIRM_WORD} autoComplete="off"
                  className="font-mono uppercase" data-testid="bulk-delete-confirm-input" />
              </div>
            )}
            {nothingToDo && (
              <p className="text-xs text-muted-foreground" data-testid="bulk-delete-nothing">
                Nothing would change: every selected {singular} has linked records and deactivation is switched off (or they are already inactive).
              </p>
            )}
          </div>
        )}

        {result && (
          <div className="space-y-3" data-testid="bulk-delete-result">
            <Alert className="border-emerald-200 bg-emerald-50/60 text-emerald-900 [&>svg]:text-emerald-600">
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>Done</AlertTitle>
              <AlertDescription className="text-emerald-900/80">
                <span data-testid="bulk-delete-result-counts">
                  {fmt(result.counts.deleted)} deleted permanently · {fmt(result.counts.deactivated)} deactivated · {fmt(result.counts.skipped)} skipped
                </span>
                {(result.scrubbed?.properties_trash || result.scrubbed?.users) ? (
                  <span className="block text-xs mt-1">
                    Cleared {result.scrubbed.properties_trash ? `${fmt(result.scrubbed.properties_trash)} recycle-bin reference(s)` : ""}
                    {result.scrubbed.properties_trash && result.scrubbed.users ? " and " : ""}
                    {result.scrubbed.users ? `${fmt(result.scrubbed.users)} inactive staff mapping(s)` : ""}.
                  </span>
                ) : null}
              </AlertDescription>
            </Alert>
            {result.skipped?.length > 0 && (
              <ul className="max-h-40 overflow-y-auto rounded-lg border divide-y text-sm" data-testid="bulk-delete-skipped-list">
                {result.skipped.map((s) => (
                  <li key={s.id} className="flex items-center justify-between gap-3 px-3 py-1.5">
                    <span className="truncate">{s.name || s.id.slice(0, 8)}</span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {s.reason === "guarded" ? "protected" : s.reason === "already_inactive" ? "already inactive" : `${fmt(s.blocking_total)} linked · skipped`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {result ? (
            <Button onClick={() => onOpenChange(false)} data-testid="bulk-delete-close">Close</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="bulk-delete-cancel">Cancel</Button>
              <Button variant={willDelete ? "destructive" : "default"} onClick={run} disabled={!canConfirm}
                className={!willDelete && willDeactivate ? "bg-amber-600 hover:bg-amber-700 text-white" : ""} data-testid="bulk-delete-confirm">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : willDelete ? <Trash2 className="h-4 w-4 mr-2" /> : <PowerOff className="h-4 w-4 mr-2" />}
                {confirmLabel}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
